<template>
<div class="px-1 pt-1">
  <div class="row mx-0">
    <div class="col-auto">
      <h2>{{ $t('page.labelPrint.form.title') }}</h2>
    </div>
    <div class="col">
      <div class="form-check m-2">
        <input class="form-check-input" type="checkbox" v-model="requestQuantityInput" id="requestQuantityInput">
        <label class="form-check-label" for="requestQuantityInput">{{ $t('action.requestQuantityInput') }}</label>
      </div>
    </div>
    <div class="d-flex justify-content-start">
      <LabelPrinterSelector class="p-2" @printer-selected="(printer: LabelPrinter | undefined) => selectedPrinter = printer"/>
      <LabelTemplateSelector class="p-2" :predefined-template-id="preferredTemplateId" @template-selected="(template: LabelTemplate | undefined) => selectedTemplate = template"/></div>
  </div>

  <ScanInput ref="scanInput" :instruction="$t('action.scanBarcode')" @scanned="barcodeScanned" class="input-group p-2" />

  <div v-if="error" class="alert alert-danger mt-2">
    {{ error }}
  </div>

  <form @submit.prevent="printLabel" v-if="currentItem" :disabled="isLoading" class="pe-1">
    <h3 class="pt-4">
      <ItemField :item="currentItem" />
    </h3>

    <div v-if="scanResultIncomplete" class="row g-3 mx-0">
      <div class="col-sm-6 col-md-4 col-lg-3">
        <label for="quantity" class="form-label">{{ $t('field.generic.quantity') }}</label>
        <div class="input-group">
          <input v-model="currentQuantity" type="number" min="1" max="10000" class="form-control" id="quantity" ref="quantityInput" :class="{ 'is-invalid': !isQuantityValid }">
          <span class="input-group-text">{{ currentItem?.baseUnitCode }}</span>
        </div>
      </div>

      <div v-if="currentItem?.trackLotNumber" class="col-sm-6 col-md-4 col-lg-3">
        <label for="lot" class="form-label">{{ $t('field.masterdata.lot') }}</label>
        <input v-model="currentLot" type="text" class="form-control" id="lot" ref="lotInput" :class="{ 'is-invalid': !isLotValid }">
      </div>

      <div v-if="currentItem?.trackBestBeforeDate" class="col-sm-6 col-md-4 col-lg-3">
        <label for="bestBefore" class="form-label">{{ $t('field.masterdata.bestBefore') }}</label>
        <LocalizedDatePicker v-model="currentBestBefore" :time-config="{ enableTimePicker: false }" :min-date="new Date().toISOString().split('T')[0]" id="bestBefore" ref="bestBeforeInput" :ui="{input: `form-control ${!isBestBeforeValid ? 'is-invalid' : ''}` }" />
      </div>

      <div v-if="currentItem?.trackExpirationDate" class="col-sm-6 col-md-4 col-lg-3">
        <label for="expiry" class="form-label">{{ $t('field.masterdata.expiry') }}</label>
        <LocalizedDatePicker v-model="currentExpiry" :time-config="{ enableTimePicker: false }" :min-date="new Date().toISOString().split('T')[0]" id="expiry" ref="expiryInput" :ui="{input: `form-control ${!isExpiryValid ? 'is-invalid' : ''}` }" />
      </div>

      <div class="d-grid gap-2">
        <button type="submit" class="btn btn-primary btn-lg d-block" :disabled="!isValid || isLoading"><font-awesome-icon :icon="['fas', 'plus']" /> {{ $t('page.labelPrint.form.confirm') }}</button>
      </div>

    </div>
  </form>
</div>
</template>

<script setup lang="ts">
import ScanInput from "@/components/form/ScanInput.vue";
import { computed, onMounted, ref, nextTick } from "vue";
import IGS1 from "@/models/IGS1";
import {
  useUserSettingsStore,
  useItemsStore,
  useTradeItemsStore,
  useItemImagesStore,
  useItemSiteSettingsStore,
  useStockLevelsStore
} from "@/stores";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { ItemField } from '@/components/fields';
import LabelPrinterSelector from "@/components/labels/LabelPrinterSelector.vue";
import LabelTemplateSelector from "@/components/labels/LabelTemplateSelector.vue";
import LocalizedDatePicker from "@/components/form/LocalizedDatePicker.vue";
import { prepareItemLabelZpl, printZpl } from "@/services/zpl.ts";
import Item from "@/models/frontend/Item";
import LabelTemplate from "@/models/frontend/LabelTemplate";
import LabelPrinter from "@/models/frontend/LabelPrinter";

const userSettingsStore = useUserSettingsStore();
const i18n = useI18n();

const isLoading = ref(false);
const error = ref('');
const scanResultIncomplete = ref(false);
const requestQuantityInput = ref(false);

const currentItem = ref<Item | undefined>();
const currentQuantity = ref<number | undefined>();
const currentLot = ref<string | undefined>();
const currentBestBefore = ref<string | undefined>();
const currentExpiry = ref<string | undefined>();
const selectedPrinter = ref<LabelPrinter | undefined>();
const selectedTemplate = ref<LabelTemplate | undefined>();
const preferredTemplateId = ref<number | undefined>();

const scanInput = ref<InstanceType<typeof ScanInput> | null>(null);
const quantityInput = ref<HTMLInputElement | null>(null);
const lotInput = ref<HTMLInputElement | null>(null);
const bestBeforeInput = ref<HTMLInputElement | null>(null);
const expiryInput = ref<HTMLInputElement | null>(null);

const itemStore = useItemsStore();
const tradeItemStore = useTradeItemsStore();
const itemImageStore = useItemImagesStore();
const itemSiteSettingStore = useItemSiteSettingsStore();
const stockLevelStore = useStockLevelsStore();

onMounted(async () => {
  requestQuantityInput.value = userSettingsStore.data.confirmQuantity ?? false;
  await stockLevelStore.fetchAll();
});

const isQuantityValid = computed(() => {
  if (!currentQuantity.value) { return false; }
  return currentQuantity.value > 0;
});

const isLotValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackLotNumber) { return true; }
  return !!currentLot.value;
});

const isBestBeforeValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackBestBeforeDate) { return true; }
  if (!currentBestBefore.value) { return false; }
  return (new Date(currentBestBefore.value) >= new Date());
});

const isExpiryValid = computed(() => {
  if (!currentItem.value) { return false; }
  if (!currentItem.value.trackExpirationDate) { return true; }
  if (!currentExpiry.value) { return false; }
  return (new Date(currentExpiry.value) >= new Date());
});

const isValid = computed(() => {
  if (!isQuantityValid.value) { return false; }
  if (!isLotValid.value) { return false; }
  if (!isBestBeforeValid.value) { return false; }
  if (!isExpiryValid.value) { return false; }
  if (!selectedPrinter.value) { return false; }
  if (!selectedTemplate.value) { return false; }
  return true;
});

const getDateString = (date: Date | undefined) => {
  if (!date) { return undefined; }
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - (offset * 60 * 1000));
  return date.toISOString().split('T')[0];
}

const barcodeScanned = async (barcode: string, gs1: IGS1 | undefined) => {
  scanResultIncomplete.value = true; // used to show/hide the form fields after successful scanning)
  currentItem.value = undefined;
  currentLot.value = undefined;
  currentBestBefore.value = undefined;
  currentExpiry.value = undefined;
  let nextErrorField = '';

  // search item
  if (barcode || gs1) {
    const contentCode = gs1?.gtin ?? barcode;
    const tradeItem = tradeItemStore.getByCode(contentCode);
    if (tradeItem) {
      currentItem.value = itemStore.getById(tradeItem.itemId);
    }
    if (!currentItem.value && gs1?.itemCode) {
      currentItem.value = itemStore.getByCode(gs1.itemCode);
    }
  }

  // if not matched already, try get item by code
  if (!currentItem.value) {
    currentItem.value = itemStore.getByCode(barcode);
  }

  if (!currentItem.value) {
    error.value = i18n.t('error.unknownItem') + ': "' + barcode + '"';
    return;
  }

  error.value = '';
  
  const setting = itemSiteSettingStore.getByItemId(currentItem.value.id);
  if (setting && !requestQuantityInput.value){
    currentQuantity.value = setting.defaultLabelQuantity;
    preferredTemplateId.value = setting.defaultLabelTemplate?.id;
  } else {
    preferredTemplateId.value = undefined;
    // this is required to invalidate the form (disabled submit button)
    currentQuantity.value = 0;
  }
  
  if (currentQuantity.value == 0){
    nextErrorField = 'quantity';
  }

  if (currentItem.value?.trackLotNumber) {
    if (gs1?.lot) {
      currentLot.value = gs1.lot;
    }
    if (!isLotValid.value && !nextErrorField) {
      nextErrorField = 'lot';
    }
  }

  if (currentItem.value.trackBestBeforeDate) {
    if (gs1?.bestBefore) {
      currentBestBefore.value = getDateString(gs1.bestBefore);
    }
    if (!isBestBeforeValid.value && !nextErrorField) {
      nextErrorField = 'bestBefore';
    }
  }

  if (currentItem.value.trackExpirationDate) {
    if (gs1?.expiry) {
      currentExpiry.value = getDateString(gs1.expiry);
    }
    if (!isExpiryValid.value && !nextErrorField) {
      nextErrorField = 'expiry';
    }
  }

  // if we did not have any error fields, directly post the receipt now
  if (!nextErrorField) {
    if (requestQuantityInput.value) {
      scanResultIncomplete.value = true;

      await nextTick(() => {
        const qIn = quantityInput.value;
        if (qIn) qIn.value = "";
        quantityInput.value?.select();
      });
      return;
    }

    // await next tick until the auto selection of e.g. the labelTemplate have occurred
    await nextTick(async () => {
      await printLabel();
    });
  }
  else {
    scanResultIncomplete.value = true;
    await nextTick(() => {
      if (nextErrorField === 'quantity') quantityInput.value?.select();
      else if (nextErrorField === 'lot') lotInput.value?.focus();
      else if (nextErrorField === 'bestBefore') bestBeforeInput.value?.focus();
      else if (nextErrorField === 'expiry') expiryInput.value?.focus();
    });
  }
};

const printLabel = async () => {
  if (!currentItem.value) return;

  isLoading.value = true;

  let content = selectedTemplate.value!.templateContent;

  const tradeItem = tradeItemStore.getBaseByItemId(currentItem.value.id);
  const stockLevel = stockLevelStore.getByItemId(currentItem.value.id);

  const imageRetrievalCallback = (imageTypeCode: string, length: number, width: number) => itemImageStore.loadItemImageAsZplByType(currentItem.value!.id, imageTypeCode, length, width);
  let tradeItemCode = tradeItem?.code;
  if (tradeItemCode !== undefined && tradeItemCode.length < 14 && tradeItemCode.length > 0) {
    tradeItemCode = tradeItemCode.padStart(14, '0');
  }
  content = await prepareItemLabelZpl(content, currentItem.value.name, tradeItemCode, currentItem.value.code, currentQuantity.value, currentLot.value, currentBestBefore.value, currentExpiry.value, stockLevel, imageRetrievalCallback);
  await printZpl(selectedPrinter.value!.address, content);

  toast.info(i18n.t('page.labelPrint.successMessage', { printer: selectedPrinter.value!.code }));

  scanResultIncomplete.value = false;
  isLoading.value = false;
  scanInput.value?.focus();
}
</script>
